import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next/src';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import Dialog from '@hobs/components/Dialog';
import Toast from '@hobs/components/Toast';
import ShortId from 'shortid';
import Tabs from '@hobs/components/Tabs';
import Icon from '@hobs/components/Icon';
import Alert from '@hobs/components/Alert';
import { email as emailValidator, phoneRegex } from '@hobs/validations';

import { softDeleteUser } from '@hobs/actions/users';
import {
  clearUser,
  getUserForSubmit,
  createUser,
  getUser,
  updateUser,
  changePassword,
  resendResetPassword,
  changeLockStatus
} from '../../../actions/users';

import './UserDialog.scss';
import UserForm from './UserForm';
import UserPasswordManager from './UserPasswordManager';

const defaultState = {
  userState: {
    error: null,
    success: null
  },
  userPasswordState: {
    error: null,
    success: null
  },
  resendDisabled: false
};

class UserDialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = defaultState;
    const { t } = props;

    this.resetPasswordSchema = Yup.object().shape({
      password: Yup.string().required(t('Password is required')),
      confirmPassword: Yup.string().required(t('Confirm password is required'))
    });
  }

  getUserSchema() {
    const { t, forSubmit } = this.props;
    return Yup.object().shape({
      firstName: Yup.string()
        .max(256, t('First name cannot be longer than 256 symbols'))
        .required(t('First name is required')),
      lastName: Yup.string()
        .max(256, t('Last name cannot be longer than 256 symbols'))
        .required(t('Last name is required')),
      email: Yup.string()
        .max(256, t('Email cannot be longer than 256 symbols'))
        .required(t('Email is required'))
        .test('emailFormat', 'Invalid email format', function(value = '') {
          return !emailValidator()(value);
        }),
      loginExpirationDate: Yup.date()
        .nullable()
        .typeError(t('Invalid date or date format'))
        .required(t('Login expiration date is required')),
      roleId: Yup.number()
        .nullable()
        .required(t('Role is required')),
      unitId: forSubmit.isUnitRequired
        ? Yup.string().required('"Assigned to" is required')
        : Yup.string(),
      phone: Yup.string()
        .matches(phoneRegex, {
          message: t('Please enter a valid phone number'),
          excludeEmptyString: true
        })
        .min(6, t('Phone number cannot be shorter than 6 symbols'))
        .max(100, t('Phone number cannot be longer than 100 symbols'))
        .nullable(),
      username: Yup.string()
        .min(2, t('Username must be at least 2 symbols'))
        .max(256, t('Username cannot be longer than 256 symbols'))
        .required(t('Username is required'))
    });
  }

  isDialogOpen() {
    return this.props.match.params.action === 'user';
  }

  getUserPathName() {
    const deleteIndex = this.props.location.pathname.indexOf('/user');
    return this.props.location.pathname.substring(0, deleteIndex);
  }

  getRoleOptions() {
    const { forSubmit } = this.props;

    if (forSubmit.roles && forSubmit.roles.length > 0) {
      return forSubmit.roles.map(role => {
        return (
          <option key={role.roleId} value={role.roleId}>
            {role.roleLongName}
          </option>
        );
      });
    }
  }

  getUnitsOptions() {
    const { units, assignedUnits } = this.props;

    let partyUnits = [];

    if (Array.isArray(assignedUnits) && assignedUnits.length > 0) {
      partyUnits = assignedUnits;
    } else {
      partyUnits = units;
    }

    const flatUnits = this.flattenUnitsTree(partyUnits);
    if (flatUnits && flatUnits.length > 0) {
      return flatUnits.map(unit => {
        return (
          <option key={ShortId.generate()} value={unit.unitId}>
            {unit.title}
          </option>
        );
      });
    }
  }

  flattenUnitsTree(array) {
    if (!Array.isArray(array)) {
      return [];
    }

    return array.reduce((result, item) => {
      result.push(item);

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units));
      }

      return result;
    }, []);
  }

  createUser = (values, { setErrors, setSubmitting }) => {
    const { t } = this.props;
    const body = { ...values };

    this.props.createUser(body).then(response => {
      if (response.success) {
        this.onDialogClose();
        Toast.success(t('User was successfully created'));
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [this.toLowercaseFirst(error.target)]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        this.setUserError(response.payload.message);
      }

      setSubmitting(false);
    });
  };

  updateUser = (values, { setErrors, setSubmitting }) => {
    const { t, onUserUpdated } = this.props;

    this.props.updateUser(values).then(response => {
      if (response.success) {
        this.props.getUser(values.userId).then(response => {
          if (response.success) {
            this.setUserSuccess(t('User was successfully updated'));
          }
        });
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [this.toLowercaseFirst(error.target)]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        this.setUserError(response.payload.message);
      }

      setSubmitting(false);
      onUserUpdated();
    });
  };

  changeUserPassword = (values, { setErrors, setSubmitting }) => {
    const { t, user } = this.props;

    this.props.changePassword(user.userId, values).then(response => {
      if (response.success) {
        this.setUserPasswordSuccess(
          t('User password was successfully changed')
        );
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [this.toLowercaseFirst(error.target)]: error.message[0]
          };
        }, {});

        setErrors(errors);
      } else if (response.payload) {
        this.setUserPasswordError(response.payload.message);
      }

      setSubmitting(false);
    });
  };

  resendResetUserPasswordEmail = () => {
    const { t, user } = this.props;

    this.setState({
      resendDisabled: true
    });

    this.props.resendResetPassword(user.userId).then(response => {
      if (response.success) {
        this.setUserPasswordSuccess(t('Password reset email has been sent.'));
      } else if (response.payload) {
        this.setUserPasswordError(response.payload.message);
      }
    });
  };

  onPasswordLockChange = value => {
    const { t, user } = this.props;

    const passwordLocked = {
      isPasswordLocked: value
    };
    this.props.changeLockStatus(user.userId, passwordLocked).then(response => {
      if (response.success) {
        this.props.getUser(user.userId).then(() => {
          this.setUserPasswordSuccess(
            t(
              value
                ? 'Account successfully locked'
                : 'Account successfully unlocked'
            )
          );
        });
      } else if (response.payload) {
        this.setUserPasswordError(response.payload.message);
      }
    });
  };

  onUserDeleted = setSubmitting => {
    const { t, user } = this.props;

    setSubmitting(true);
    this.props.softDeleteUser(user.userId).then(response => {
      if (response.success) {
        this.onDialogClose();
        Toast.success(t('User deleted successfully.'));
      } else if (response.payload) {
        Toast.error(response.payload.message);
      }
      setSubmitting(false);
    });
  };

  toLowercaseFirst(value) {
    if (typeof value !== 'string' || value.length === 0) {
      return value;
    }

    return value.charAt(0).toLowerCase() + value.slice(1);
  }

  onDialogClose = () => {
    this.resetState();
    this.props.clearUser();
    this.props.reloadGrid();
    this.props.history.push(this.getUserPathName());
  };

  onDialogOpening = () => {
    this.fetchRequiredData();
  };

  fetchRequiredData() {
    this.props.getUserForSubmit();
    if (!this.props.user) {
      const userId = this.props.match.params.guid;
      if (userId) {
        this.props.getUser(userId);
      }
    }
  }

  setUserError(message) {
    this.setState({
      userState: {
        error: message,
        success: null
      }
    });
  }

  setUserPasswordError(message) {
    this.setState({
      userPasswordState: {
        error: message,
        success: null
      }
    });
  }

  setUserSuccess(message) {
    this.setState({
      userState: {
        error: null,
        success: message
      }
    });
  }

  setUserPasswordSuccess(message) {
    this.setState({
      userPasswordState: {
        error: null,
        success: message
      }
    });
  }

  resetState() {
    this.setState(defaultState);
  }

  isEdit() {
    const { match, user } = this.props;
    return match.params.guid || user;
  }

  render() {
    const { t, user, partyId } = this.props;
    const { userState, userPasswordState, resendDisabled } = this.state;

    return (
      <Dialog
        title={this.isEdit() ? t('Edit user') : t('Create user')}
        isOpen={this.isDialogOpen()}
        onClose={this.onDialogClose}
        onOpening={this.onDialogOpening}
        className="user-create"
      >
        {!this.isEdit() && (
          <>
            {userState.error && <Alert type="error">{userState.error}</Alert>}
            <UserForm
              enableReinitialize={false}
              userSchema={this.getUserSchema()}
              partyId={partyId}
              unitOptions={this.getUnitsOptions()}
              roleOptions={this.getRoleOptions()}
              onSubmit={this.createUser}
              isCreation={true}
            />
          </>
        )}
        {this.isEdit() && (
          <Tabs className="user-tabs">
            <Tabs.Tab
              component={
                <>
                  {userState.error && (
                    <Alert type="error">{userState.error}</Alert>
                  )}
                  {userState.success && (
                    <Alert type="success">{userState.success}</Alert>
                  )}
                  <UserForm
                    enableReinitialize={true}
                    userSchema={this.userSchema}
                    partyId={partyId}
                    unitOptions={this.getUnitsOptions()}
                    roleOptions={this.getRoleOptions()}
                    user={user}
                    onSubmit={this.updateUser}
                  />
                </>
              }
            >
              {<Icon icon="Pen" />}
            </Tabs.Tab>
            <Tabs.Tab
              component={
                <>
                  {userPasswordState.error && (
                    <Alert type="error">{userPasswordState.error}</Alert>
                  )}
                  {userPasswordState.success && (
                    <Alert type="success">{userPasswordState.success}</Alert>
                  )}
                  <UserPasswordManager
                    resetPasswordSchema={this.resetPasswordSchema}
                    user={user}
                    onResetPasswordSubmit={this.changeUserPassword}
                    onResendPasswordSubmit={this.resendResetUserPasswordEmail}
                    onPasswordLockChange={this.onPasswordLockChange}
                    onUserDeleted={this.onUserDeleted}
                    resendDisabled={resendDisabled}
                  />
                </>
              }
            >
              {<Icon icon="Settings" />}
            </Tabs.Tab>
          </Tabs>
        )}
      </Dialog>
    );
  }
}

UserDialog.propTypes = {
  t: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  reloadGrid: PropTypes.func,
  forSubmit: PropTypes.object,
  createUser: PropTypes.func,
  getUserForSubmit: PropTypes.func,
  getUser: PropTypes.func,
  updateUser: PropTypes.func,
  partyId: PropTypes.string,
  units: PropTypes.array,
  user: PropTypes.object,
  clearUser: PropTypes.func,
  resendResetPassword: PropTypes.func,
  changePassword: PropTypes.func,
  softDeleteUser: PropTypes.func,
  changeLockStatus: PropTypes.func,
  assignedUnits: PropTypes.array,
  onUserUpdated: PropTypes.func
};

const mapStateToProps = state => ({
  forSubmit: state.users.userForSubmit,
  user: state.users.user
});

const mapDispatchToProps = {
  createUser,
  getUserForSubmit,
  getUser,
  updateUser,
  clearUser,
  resendResetPassword,
  changePassword,
  softDeleteUser,
  changeLockStatus
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(UserDialog));
